import {
  action,
  computed,
  makeAutoObservable,
  observable,
  runInAction,
} from "mobx";

import {
  BuildSituation,
  Button,
  Consultant,
  DisplayLocation,
  Estate,
  ExteriorStyle,
  Home,
  HouseLand,
  HouseType,
  InteriorStyle,
  ProcessStep,
  Region,
  Series,
  SortMethod,
  User,
} from "../types";
import { NavigationStore } from "./NavigationStore";
import { UserStore } from "./UserStore";

/**
 * API Store contains app state used for interacting with the API - such as filter selections and api call results
 */
export class ApiStore {
  @observable isLoading = false;
  @observable isLoadingNextPage = false;

  @observable region: Region | null = null;
  @observable estate: Estate | null = null;
  @observable estateOther = "";
  @observable lotWidth = "";
  @observable houseType: HouseType | null = null;
  @observable buildSituation: BuildSituation | null = null;
  @observable series: Series | null = null;
  @observable selectedSeries: Series | null = null;

  @observable grantSettings = {
    grant_enabled: false,
    grant_label: "",
    grant_name: "",
    grant_amount: 0,
  };

  @observable finalDetailsSettings = {
    promo_enabled: false,
    promo_desktop_image: "",
    promo_mobile_image: "",
    promo_link: "",
    promo_link_text: "",
    further_customise_enabled: false,
    further_customise_copy: "",
    thank_you_enabled: false,
    thank_you_copy: "",
    disclaimer_copy: "",
    kiosk_footer_copy: "",
    kiosk_thank_you_copy: "",
    print_logo: "",
    form_disclaimer: "",
    finance_cta_title: "",
    summary_popup_show: false,
    summary_popup_title: "",
    summary_popup_copy: "",
    summary_popup_image: "",
  };

  @observable finalDetailsPriceDisclaimers: string[] = [];
  @observable finalDetailsFinanceCtaButton: Button | null = null;
  @observable finalDetailsSummaryPopupButton: Button | null = null;

  @observable processSettings = {
    title: "",
    copy: "",
    steps: [],
    video_file: "",
    video_url: "",
    image: "",
  };

  @observable processSteps: ProcessStep[] = [];

  @observable simpleDetailsSettings = {
    copy: "",
  };

  @observable chooseFloorplanSettings = {
    promo_desktop_image: "",
    promo_mobile_image: "",
  };

  @observable kioskSplash = "";

  // Home Filters values
  @observable numBeds = 0;
  @observable numLiving = 0;
  @observable numBaths = 0;
  @observable numCars = 0;
  @observable searchText = "";
  @observable minBudget = 0;
  @observable maxBudget = 0;
  @observable minLotSize: number | null = null;
  @observable maxLotSize: number | null = null;
  @observable sortMethod = SortMethod.SIZE_DESC;

  // Drop Down Filters
  @observable.deep lotWidthsResults: string[] = [];
  @observable.deep lotSizesResults: string[] = [];
  @observable.deep houseSizesResults: string[] = [];
  @observable.deep pricesResults: string[] = [];
  @observable.deep hlPricesResults: string[] = [];
  @observable.deep seriesOptions: Series[] = [];

  @observable.deep regionResults: Region[] = [];
  @observable.deep consultantResults: Consultant[] = [];
  @observable.deep nswRegionResults: Region[] = [];

  @observable homeSearchResultsPaged: Record<number, Home[]> = {};
  @observable seriesSearchResultsPaged: Record<number, Series[]> = {};
  @observable homeSearchPageSize = 5;
  @observable homeSearchTotalResults = 0;
  @observable homeSearchLastPage = 1;
  @observable homeSearchCurrentPage = 1;

  @observable.deep houseLandSearchResults: HouseLand[] = [];
  @observable.deep exteriorStyleResults: ExteriorStyle[] = [];
  @observable.deep interiorStyleResults: InteriorStyle[] = [];

  // If the user entered the app via a link from a home on the main website, this is it
  @observable enteredViaHome: Home | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  priceSort(items: Home[] | HouseLand[]) {
    const clone = [...items];
    if (this.sortMethod === SortMethod.PRICE_ASC) {
      clone.sort((a, b) => {
        if (a.price < b.price) return -1;
        if (a.price > b.price) return 1;
        return 0;
      });
    } else if (this.sortMethod === SortMethod.PRICE_DESC) {
      clone.sort((a, b) => {
        if (a.price > b.price) return -1;
        if (a.price < b.price) return 1;
        return 0;
      });
    }
    return clone;
  }

  @computed get rendivoDisclaimerVisible() {
    // Disclaimer should disappear on the 1st of Aug 2022
    const endDate = new Date("2022-08-01T00:00:00");
    const now = new Date();
    return now <= endDate;
  }

  @computed
  get regionsKeyedById() {
    const regionMap: Record<string, Region> = {};
    for (const region of this.regionResults) {
      regionMap[region.id] = region;
    }
    return regionMap;
  }

  @computed
  get nswRegionsKeyedById() {
    const regionMap: Record<string, Region> = {};
    for (const region of this.nswRegionResults) {
      regionMap[region.id] = region;
    }
    return regionMap;
  }

  @computed
  get consultantsKeyedById() {
    const consultantMap: Record<string, Consultant> = {};
    for (const consultant of this.consultantResults) {
      consultantMap[consultant.id] = consultant;
    }
    return consultantMap;
  }

  @computed
  get homeSearchResultsWithPlaceholders(): (Home | null)[] {
    const homes: (Home | null)[] = [];

    for (let pageNum = 1; pageNum <= this.homeSearchLastPage; pageNum++) {
      if (this.homeSearchResultsPaged[pageNum] !== undefined) {
        for (const home of this.homeSearchResultsPaged[pageNum]) {
          homes.push(home);
        }
      } else {
        for (let i = 0; i < this.homeSearchPageSize; i++) {
          homes.push(null);
        }
      }
    }
    return homes;
  }

  @computed
  get seriesSearchResultsWithPlaceholders(): (Series | null)[] {
    const series: (Series | null)[] = [];

    for (let pageNum = 1; pageNum <= this.homeSearchLastPage; pageNum++) {
      if (this.seriesSearchResultsPaged[pageNum] !== undefined) {
        for (const serie of this.seriesSearchResultsPaged[pageNum]) {
          series.push(serie);
        }
      } else {
        for (let i = 0; i < this.homeSearchPageSize; i++) {
          series.push(null);
        }
      }
    }
    return series;
  }

  @computed
  get houseLandSearchResultsSorted(): Home[] {
    return this.priceSort(this.houseLandSearchResults);
  }

  @computed
  get houseLandSearchQueryString() {
    const params: Record<string, string> = {};

    if (this.houseType !== null) {
      params.type = this.houseType;
    }

    if (this.estate != null && this.estate.id !== "other") {
      params.estate = this.estate.id;
    } else if (this.region != null) {
      params.region = this.region.id;
    }

    params.sort = this.sortMethod;

    if (this.numBeds > 0) {
      params.num_beds = this.numBeds.toString(10);
    }
    if (this.numBaths > 0) {
      params.num_baths = this.numBaths.toString(10);
    }
    if (this.numCars > 0) {
      params.num_cars = this.numCars.toString(10);
    }
    if (this.numLiving > 0) {
      params.num_living = this.numLiving.toString(10);
    }
    if (this.minBudget > 0) {
      params.min_budget = this.minBudget.toString(10);
    }
    if (this.maxBudget > 0) {
      params.max_budget = this.maxBudget.toString(10);
    }
    if (this.minLotSize !== null) {
      params.min_lot_size = this.minLotSize.toString(10);
    }
    if (this.maxLotSize !== null && this.maxLotSize > 0) {
      params.max_lot_size = this.maxLotSize.toString(10);
    }

    if (this.enteredViaHome) {
      params.home_id = this.enteredViaHome.id.toString(10);
    }

    const searchParams = new URLSearchParams(params);
    return searchParams.toString();
  }

  @action
  reset() {
    this.houseType = null;
    this.buildSituation = null;
    this.homeSearchResultsPaged = {};
    this.seriesSearchResultsPaged = {};
    this.enteredViaHome = null;
    this.resetHouseFilters();
    this.resetLandFilters();
    this.resetHomeSearchPagination();
  }

  @action
  resetLandFilters() {
    this.region = null;
    this.estate = null;
    this.estateOther = "";
    this.lotWidth = "";
  }

  @action
  resetHouseFilters() {
    this.numBeds = 0;
    this.numLiving = 0;
    this.numBaths = 0;
    this.numCars = 0;
    this.searchText = "";
    this.minBudget = 0;
    this.maxBudget = 0;
    this.minLotSize = 0;
    this.maxLotSize = 0;
    this.series = null;
  }

  @action
  setIsLoading(isLoading: boolean) {
    this.isLoading = isLoading;
  }

  @action
  setRegion(region: Region | null) {
    this.region = region;
  }

  @action
  setEstate(estate: Estate | null) {
    this.estate = estate;
  }

  @action
  setEstateOther(estateOther: string) {
    this.estateOther = estateOther;
  }

  @action
  setLotWidth(lotWidth: string) {
    this.lotWidth = lotWidth;
  }

  @action
  setSeries(id: string) {
    let found = false;
    for (const series of this.seriesOptions) {
      if(series.id == id){
        this.series = series;
        found = true;
      }
    }
    if(!found){
      this.series = null;
    }
  }

  @action
  setSelectedSeries(series: Series | null) {
    this.selectedSeries = series;
  }

  @action
  setEnteredViaHome(home: Home | null) {
    this.enteredViaHome = home;
  }

  @action
  setHouseType(houseType: HouseType | null) {
    this.houseType = houseType;
  }

  @action
  toggleHouseType(houseType: HouseType) {
    if(this.houseType === houseType){
      this.houseType = null;
    }else{
      this.houseType = houseType;
    }
  }

  @action
  setBuildSituation(buildSituation: BuildSituation | null) {
    this.buildSituation = buildSituation;
  }

  @action
  setNumBeds(val: number) {
    this.numBeds = val;
  }

  @action
  toggleNumBeds(val: number) {
    if (this.numBeds !== val) {
      this.numBeds = val;
    } else {
      this.numBeds = 0;
    }
  }

  @action
  setNumLiving(val: number) {
    this.numLiving = val;
  }

  @action
  toggleNumLiving(val: number) {
    if (this.numLiving !== val) {
      this.numLiving = val;
    } else {
      this.numLiving = 0;
    }
  }

  @action
  setNumBaths(val: number) {
    this.numBaths = val;
  }

  @action
  toggleNumBaths(val: number) {
    if (this.numBaths !== val) {
      this.numBaths = val;
    } else {
      this.numBaths = 0;
    }
  }

  @action
  setNumCars(val: number) {
    this.numCars = val;
  }

  @action
  toggleNumCars(val: number) {
    if (this.numCars !== val) {
      this.numCars = val;
    } else {
      this.numCars = 0;
    }
  }

  @action
  setSearchText(val: string) {
    this.searchText = val;
  }

  @action
  setMinBudget(val: number) {
    this.minBudget = val;
  }

  @action
  setMaxBudget(val: number) {
    this.maxBudget = val;
  }

  @action
  setMinLotSize(val: number | null) {
    this.minLotSize = val;
  }

  @action
  setMaxLotSize(val: number | null) {
    this.maxLotSize = val;
  }

  @action
  setSortMethod(val: SortMethod) {
    this.sortMethod = val;
  }

  @action
  setHomeSearchCurrentPage(val: number) {
    this.homeSearchCurrentPage = val;
  }

  @action setInitialAppData(
    settings: any,
    regions: Region[],
    nswRegions: Region[],
    consultants: Consultant[],
    exteriorStyles: ExteriorStyle[],
    interiorStyles: InteriorStyle[],
    series: Series[],
  ) {

    this.lotWidthsResults = settings.lot_widths.map((val: number) =>
      val.toString(10)
    );

    this.lotSizesResults = settings.lot_sizes.map((val: number) =>
      val.toString(10)
    );

    this.houseSizesResults = settings.house_sizes.map((val: number) =>
      val.toString(10)
    );

    this.seriesOptions = series;

    this.pricesResults = settings.prices.map((val: number) => val.toString(10));
    
    this.hlPricesResults = settings.hl_prices.map((val: number) => val.toString(10));

    this.regionResults = regions.map((item) => {
      return {
        ...item,
        // We need the IDs as strings as they will be used in Select components
        id: item.id.toString(),
        // @ts-ignore
        estates: item.estates.map((estate) => {
          return {
            ...estate,
            id: estate.id.toString(),
          };
        }),
      };
    });

    this.nswRegionResults = nswRegions.map((item) => {
      return {
        ...item,
        // We need the IDs as strings as they will be used in Select components
        id: item.id.toString(),
        estates: [],
      };
    });

    this.consultantResults = consultants.map((item) => {
      return {
        ...item,
        // We need the IDs as strings as they will be used in Select components
        id: item.id.toString(),
      };
    });

    this.exteriorStyleResults = exteriorStyles;
    this.interiorStyleResults = interiorStyles;

    this.grantSettings.grant_enabled = settings.pyht_grant_enabled;
    this.grantSettings.grant_label = settings.pyht_grant_label;
    this.grantSettings.grant_name = settings.pyht_grant_name;
    this.grantSettings.grant_amount = settings.pyht_grant_amount;

    this.finalDetailsSettings.promo_enabled = settings.pyht_final_promo_enabled;
    this.finalDetailsSettings.promo_desktop_image = settings.pyht_final_promo_desktop;
    this.finalDetailsSettings.promo_mobile_image = settings.pyht_final_promo_mobile;
    this.finalDetailsSettings.promo_link = settings.pyht_final_promo_link;
    this.finalDetailsSettings.promo_link_text = settings.pyht_final_promo_link_text;
    this.finalDetailsSettings.further_customise_enabled = settings.pyht_further_customise_enabled;
    this.finalDetailsSettings.further_customise_copy = settings.pyht_further_customise_copy;
    this.finalDetailsSettings.thank_you_enabled = settings.pyht_thank_you_enabled;
    this.finalDetailsSettings.thank_you_copy = settings.pyht_thank_you_copy;
    this.finalDetailsSettings.disclaimer_copy = settings.pyht_disclaimer_copy;
    this.finalDetailsSettings.kiosk_footer_copy = settings.pyht_kiosk_footer_copy;
    this.finalDetailsSettings.kiosk_thank_you_copy = settings.pyht_kiosk_thank_you_copy;
    this.kioskSplash = settings.pyht_kiosk_splash;
    this.chooseFloorplanSettings.promo_desktop_image = settings.pyht_start_promo_desktop;
    this.chooseFloorplanSettings.promo_mobile_image = settings.pyht_start_promo_mobile;
    this.finalDetailsSettings.print_logo = settings.pyht_print_logo;
    this.simpleDetailsSettings.copy = settings.pyht_simple_details_copy;
    this.finalDetailsSettings.form_disclaimer = settings.pyht_form_disclaimer;
    this.finalDetailsSettings.finance_cta_title = settings.pyht_finance_cta_title;
    this.finalDetailsPriceDisclaimers = settings.pyht_price_disclaimers;
    this.finalDetailsFinanceCtaButton = settings.pyht_finance_cta_button;
    this.processSettings.title = settings.pyht_process_title;
    this.processSettings.copy = settings.pyht_process_copy;
    this.processSteps = settings.pyht_process_steps;
    this.processSettings.video_file = settings.pyht_process_video_file;
    this.processSettings.video_url = settings.pyht_process_video_url;
    this.processSettings.image = settings.pyht_process_image;
    this.finalDetailsSettings.summary_popup_show = settings.pyht_summary_popup_show;
    this.finalDetailsSettings.summary_popup_title = settings.pyht_summary_popup_title;
    this.finalDetailsSettings.summary_popup_copy = settings.pyht_summary_popup_copy;
    this.finalDetailsSettings.summary_popup_image = settings.pyht_summary_popup_image;
    this.finalDetailsSummaryPopupButton = settings.pyht_summary_popup_button;
  }

  @action resetHomeSearchPagination() {
    this.homeSearchTotalResults = 0;
    this.homeSearchLastPage = 1;
    this.homeSearchCurrentPage = 1;
    this.homeSearchResultsPaged = {};
    this.seriesSearchResultsPaged = {};
  }

  @action
  async getSeries(page?: number, hasSortChanged?: boolean) {
    const params: Record<string, string> = {};
    
    if (this.houseType !== null) {
      params.type = this.houseType;
    }

    if (this.numBeds > 0) {
      params.num_beds = this.numBeds.toString(10);
    }
    if (this.numBaths > 0) {
      params.num_baths = this.numBaths.toString(10);
    }
    if (this.numCars > 0) {
      params.num_cars = this.numCars.toString(10);
    }
    if (this.numLiving > 0) {
      params.num_living = this.numLiving.toString(10);
    }
    if (this.searchText !== "") {
      params.search = this.searchText;
    }

    if (this.lotWidth !== "" && this.lotWidth !== "any") {
      params.lot_width = this.lotWidth;
    }

    if (this.series !== null) {
      params.series = this.series.id;
    }

    params.sort = this.sortMethod;

    if (page === undefined) {
      page = 1;
      if (this.enteredViaHome) {
        params.focus_home = this.enteredViaHome.id.toString(10);
      }
    } else {
      this.isLoadingNextPage = true;
    }
    if (hasSortChanged === undefined) {
      params.best_seller = "1";
    }else{
      if(hasSortChanged){
        params.best_seller = "";
      }else{
        params.best_seller = "1";
      }
    }
    this.isLoading = true;

    params.page = page.toString(10);

    try {
      // this.isLoading = true;
      const searchParams = new URLSearchParams(params);
      const response = await fetch(
        `/pyht/series?${searchParams.toString()}`
      );
      const result = await response.json();

      runInAction(() => {
        this.homeSearchTotalResults = result.total;
        this.homeSearchLastPage = result.last_page;
        this.homeSearchPageSize = result.per_page;
        this.homeSearchCurrentPage = result.current_page;

        this.seriesSearchResultsPaged[result.current_page] = result.items;

        this.isLoading = false;
        this.isLoadingNextPage = false;
      });
    } catch (e) {
      console.error(e);
      runInAction(() => {
        this.seriesSearchResultsPaged = {};
        this.isLoading = false;
        this.isLoadingNextPage = false;
      });
    }
  }

  @action
  async getSingleHome(id: number, userContext: UserStore, navContext: NavigationStore) {
    const params: Record<string, string> = {};
    
    params.id = id.toString(10);

    try {
      this.isLoading = true;
      navContext.goNext();
      const searchParams = new URLSearchParams(params);
      const response = await fetch(
        `/pyht/home?${searchParams.toString()}`
      );
      const result = await response.json();
      
      runInAction(() => {
        this.isLoading = false;
        userContext.selectedHome = result.home;
        userContext.selectedFacade = result.home.facades ? result.home.facades[0] : null;
      });
    } catch (e) {
      console.error(e);
      runInAction(() => {
        this.isLoading = false;
        userContext.selectedHome = null;
      });
    }
  }

  @action
  async getHouseLand() {
    try {
      this.isLoading = true;
      const result = await fetch(
        `/pyht/house-land?${this.houseLandSearchQueryString}`
      );
      const items = await result.json();
      runInAction(() => {
        this.houseLandSearchResults = items;
        this.isLoading = false;
      });
    } catch (e) {
      console.error(e);
      runInAction(() => {
        this.houseLandSearchResults = [];
        this.isLoading = false;
      });
    }
  }

  @action
  async loginUser(user: {
    email: string;
    password: string;
  }): Promise<{ errors?: any; error?: string; user?: User }> {
    try {
      this.isLoading = true;
      const response = await fetch("/pyht/user/login", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(user),
      });
      const result = await response.json();
      runInAction(() => {
        this.isLoading = false;
      });
      return result;
    } catch (e) {
      console.error(e);
      runInAction(() => {
        this.isLoading = false;
      });
      throw e;
    }
  }

  @action
  async logoutUser(): Promise<void> {
    try {
      this.isLoading = true;
      await fetch("/pyht/user/logout", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });
      runInAction(() => {
        this.isLoading = false;
      });
    } catch (e) {
      console.error(e);
      runInAction(() => {
        this.isLoading = false;
      });
      throw e;
    }
  }

  @action
  async registerUser(
    user: User
  ): Promise<{ errors?: any; error?: string; user?: User }> {
    try {
      this.isLoading = true;
      const response = await fetch("/pyht/user/register", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(user),
      });
      const result = await response.json();
      runInAction(() => {
        this.isLoading = false;
      });
      return result;
    } catch (e) {
      console.error(e);
      runInAction(() => {
        this.isLoading = false;
      });
      throw e;
    }
  }

  @action
  async saveAppState(uuid: string, appStateJson: string) {
    try {
      await fetch(`/pyht/app-state/${uuid}`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: appStateJson,
      });
    } catch (e) {
      console.error(e);
    }
  }
  
  @action
  async emailSummary(user: User): Promise<{ errors?: any; error?: string; success?: boolean }> {
    try {
      this.isLoading = true;
      const response = await fetch("/pyht/user/email-summary", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(user),
      });
      const result = await response.json();
      runInAction(() => {
        this.isLoading = false;
      });
      return result;
    } catch (e) {
      console.error(e);
      runInAction(() => {
        this.isLoading = false;
      });
      throw e;
    }
  }

  @action
  async simpleDetails(user: User): Promise<{ errors?: any; error?: string; success?: boolean }> {
    try {
      this.isLoading = true;
      const response = await fetch("/pyht/user/simple-details", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(user),
      });
      const result = await response.json();
      runInAction(() => {
        this.isLoading = false;
      });
      return result;
    } catch (e) {
      console.error(e);
      runInAction(() => {
        this.isLoading = false;
      });
      throw e;
    }
  }
}
